// LiquidityPoolDialog.jsx

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  Chip,
  InputAdornment,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import DeleteIcon from "@mui/icons-material/Delete";

/* ─────────────────────────── CONSTANTS ─────────────────────────── */

const POOL_TYPES = [
  { value: "checking", label: "Checking Account" },
  { value: "savings", label: "Savings Account" },
  { value: "money_market", label: "Money Market" },
  { value: "certificate_of_deposit", label: "Certificate of Deposit (CD)" },
  { value: "treasury_account", label: "Treasury Account" },
  { value: "investment_account", label: "Investment Account" },
  { value: "escrow", label: "Escrow Account" },
  { value: "other", label: "Other" },
];

const POOL_STATUSES = [
  { value: "active", label: "Active", color: "success" },
  { value: "inactive", label: "Inactive", color: "default" },
  { value: "matured", label: "Matured", color: "info" },
  { value: "closed", label: "Closed", color: "error" },
];

/* numeric fields we should cast → number  (empty → omit) */
const NUMERIC_FIELDS = ["balance", "interest_rate"];

/* Helper – strip empty strings & cast numerics */
const cleanPayload = (raw) => {
  const out = {};
  Object.entries(raw).forEach(([k, v]) => {
    if (v === "" || v === null || v === undefined) return;
    if (NUMERIC_FIELDS.includes(k)) {
      const num = Number(v);
      if (!Number.isNaN(num)) out[k] = num;
      return;
    }
    out[k] = v;
  });
  return out;
};

const formatCurrency = (amount) => {
  if (!amount) return "$0.00";
  return `$${parseFloat(amount).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/* -------------------------------------------------------------------------- */
/*                              LiquidityPoolForm                             */
/* -------------------------------------------------------------------------- */
const LiquidityPoolForm = ({ initialValues, onSave, onCancel }) => {
  const [form, setForm] = useState(initialValues);
  const isEdit = Boolean(initialValues?.id);

  useEffect(() => setForm(initialValues), [initialValues]);

  const handleChange = (key) => (e) =>
    setForm({ ...form, [key]: e.target.value });

  const handleSubmit = () => onSave(cleanPayload(form));

  const requiresMaturityDate =
    form.pool_type === "certificate_of_deposit" ||
    form.pool_type === "treasury_account";

  return (
    <>
      <DialogContent dividers>
        <Box sx={{ display: "grid", gap: 2 }}>
          {/* Account Name & Type */}
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: "1fr 1fr",
            }}
          >
            <TextField
              label="Account Name"
              value={form.account_name}
              onChange={handleChange("account_name")}
              required
              fullWidth
              helperText="Nickname or identifier for this account"
            />

            <FormControl fullWidth required>
              <InputLabel>Pool Type</InputLabel>
              <Select
                label="Pool Type"
                value={form.pool_type}
                onChange={handleChange("pool_type")}
              >
                {POOL_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Bank Information */}
          <Typography variant="subtitle2" sx={{ mt: 1, color: "#6b7280" }}>
            Bank Information
          </Typography>

          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: "2fr 1fr",
            }}
          >
            <TextField
              label="Bank Name"
              value={form.bank_name}
              onChange={handleChange("bank_name")}
              required
              fullWidth
            />

            <FormControl fullWidth required>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={form.status}
                onChange={handleChange("status")}
              >
                {POOL_STATUSES.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Account Details */}
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: "1fr 1fr 1fr",
            }}
          >
            <TextField
              label="Account Number"
              value={form.account_number ?? ""}
              onChange={handleChange("account_number")}
              fullWidth
            />

            <TextField
              label="ABA Routing Number"
              value={form.aba_routing_number ?? ""}
              onChange={handleChange("aba_routing_number")}
              fullWidth
              inputProps={{ maxLength: 9 }}
              helperText="9-digit routing number"
            />

            <TextField
              label="SWIFT Code"
              value={form.swift_code ?? ""}
              onChange={handleChange("swift_code")}
              fullWidth
              inputProps={{ maxLength: 11 }}
              helperText="For international accounts"
            />
          </Box>

          {/* Financial Details */}
          <Typography variant="subtitle2" sx={{ mt: 1, color: "#6b7280" }}>
            Financial Details
          </Typography>

          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: requiresMaturityDate ? "1fr 1fr 1fr" : "1fr 1fr",
            }}
          >
            <TextField
              label="Current Balance"
              value={form.balance ?? ""}
              onChange={handleChange("balance")}
              type="number"
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
            />

            <TextField
              label="Interest Rate"
              value={form.interest_rate ?? ""}
              onChange={handleChange("interest_rate")}
              type="number"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">%</InputAdornment>
                ),
              }}
              helperText="Annual percentage rate"
            />

            {requiresMaturityDate && (
              <TextField
                label="Maturity Date"
                value={form.maturity_date ?? ""}
                onChange={handleChange("maturity_date")}
                type="date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                required={requiresMaturityDate}
              />
            )}
          </Box>

          {/* Last Verified Date */}
          <TextField
            label="Last Verified Date"
            value={form.last_verified_date ?? ""}
            onChange={handleChange("last_verified_date")}
            type="date"
            InputLabelProps={{ shrink: true }}
            fullWidth
            helperText="When was this balance last confirmed?"
          />

          {/* Notes */}
          <TextField
            label="Notes"
            value={form.notes ?? ""}
            onChange={handleChange("notes")}
            multiline
            rows={3}
            fullWidth
            helperText="Any additional information about this account"
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="contained"
          disabled={
            !form.pool_type ||
            !form.account_name ||
            !form.bank_name ||
            (requiresMaturityDate && !form.maturity_date)
          }
          onClick={handleSubmit}
        >
          {isEdit ? "Save Changes" : "Create Pool"}
        </Button>
      </DialogActions>
    </>
  );
};

/* -------------------------------------------------------------------------- */
/*                          LiquidityPoolDialog Shell                         */
/* -------------------------------------------------------------------------- */
const LiquidityPoolDialog = ({
  open,
  pools = [],
  onCreate,
  onUpdate,
  onDelete,
  onClose,
}) => {
  const [mode, setMode] = useState("list");
  const [editing, setEditing] = useState(null);

  const reset = () => {
    setMode("list");
    setEditing(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleCreate = (payload) => {
    onCreate(payload);
    reset();
  };

  const handleUpdate = (payload) => {
    onUpdate({ id: editing.id, payload });
    reset();
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this liquidity pool?")) {
      onDelete(id);
    }
  };

  const totalBalance = pools
    .filter((p) => p.status === "active")
    .reduce((sum, p) => sum + parseFloat(p.balance || 0), 0);

  /* ============= ADD / EDIT SCREEN ============= */
  if (mode !== "list") {
    return (
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccountBalanceIcon sx={{ color: "#2563eb" }} />
            {mode === "add" ? "Add Liquidity Pool" : "Edit Liquidity Pool"}
          </Box>
        </DialogTitle>
        <LiquidityPoolForm
          initialValues={
            mode === "edit"
              ? editing
              : {
                  pool_type: "",
                  status: "active",
                  account_name: "",
                  bank_name: "",
                  account_number: "",
                  aba_routing_number: "",
                  swift_code: "",
                  balance: "",
                  interest_rate: "",
                  maturity_date: "",
                  notes: "",
                  last_verified_date: "",
                }
          }
          onSave={mode === "add" ? handleCreate : handleUpdate}
          onCancel={() => setMode("list")}
        />
      </Dialog>
    );
  }

  /* ================= LIST SCREEN ================= */
  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xl">
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccountBalanceIcon sx={{ color: "#2563eb" }} />
            <Typography variant="h6">Liquidity Pools</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Box sx={{ textAlign: "right" }}>
              <Typography variant="caption" color="text.secondary">
                Total Active Balance
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#2563eb" }}
              >
                {formatCurrency(totalBalance)}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setMode("add")}
            >
              Add Pool
            </Button>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {pools.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No liquidity pools configured yet.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setMode("add")}
              sx={{ mt: 2 }}
            >
              Add Your First Pool
            </Button>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Account Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Bank</TableCell>
                <TableCell>Account #</TableCell>
                <TableCell>Routing #</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell align="right">Interest Rate</TableCell>
                <TableCell>Maturity</TableCell>
                <TableCell>Last Verified</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pools.map((pool) => {
                const poolType = POOL_TYPES.find(
                  (t) => t.value === pool.pool_type
                );
                const poolStatus = POOL_STATUSES.find(
                  (s) => s.value === pool.status
                );

                return (
                  <TableRow key={pool.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {pool.account_name}
                      </Typography>
                    </TableCell>
                    <TableCell>{poolType?.label || pool.pool_type}</TableCell>
                    <TableCell>{pool.bank_name}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                        {pool.account_number
                          ? `***${pool.account_number.slice(-4)}`
                          : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                        {pool.aba_routing_number || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: pool.status === "active" ? "#10b981" : "#6b7280",
                        }}
                      >
                        {formatCurrency(pool.balance)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {pool.interest_rate ? `${pool.interest_rate}%` : "-"}
                    </TableCell>
                    <TableCell>
                      {pool.maturity_date
                        ? new Date(pool.maturity_date).toLocaleDateString()
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {pool.last_verified_date
                        ? new Date(pool.last_verified_date).toLocaleDateString()
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={poolStatus?.label || pool.status}
                        color={poolStatus?.color || "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditing(pool);
                              setMode("edit");
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(pool.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LiquidityPoolDialog;